import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { describeZoneTiming } from '../utils/spiralOverlays';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert,
  RadioGroup, FormControlLabel, Radio, Select, MenuItem, InputLabel, FormControl, Chip,
} from '@mui/material';

/**
 * Chooses which spiral question to ask before running anything.
 *
 * <h2>Why this exists</h2>
 *
 * All three analyses already existed, but two of them were reachable only by right-clicking a
 * candle - a gesture with no affordance anywhere on screen. Features you cannot find are features
 * you do not have, so the toolbar button now opens this instead of silently picking one of the
 * three on the user's behalf. The right-click route still works and is named at the bottom, which
 * is how it becomes discoverable rather than folklore.
 *
 * <h2>The three questions</h2>
 *
 * <ul>
 *   <li><b>UPCOMING</b> — where is the next convergence, and which turns feed it? The scan.</li>
 *   <li><b>FORWARD</b> — this turn casts a ladder; did the market turn at those dates?</li>
 *   <li><b>BACKWARD</b> — which earlier turns point at this bar? The same analysis the upcoming
 *       zone gets, aimed at a historical bar instead.</li>
 * </ul>
 */

export const SPIRAL_MODES = {
  UPCOMING: 'UPCOMING',
  /**
   * The same near-term window, centred on the last bar the reader clicked rather than on today.
   *
   * <p>A separate mode rather than a checkbox on UPCOMING, because it needs a re-scan: the window
   * is applied server-side, so moving its centre means asking the backend again with an anchor. It
   * is also a different question — "what is due now" and "what surrounded that turn" have different
   * answers and both are worth asking.
   */
  UPCOMING_AT_BAR: 'UPCOMING_AT_BAR',
  FORWARD: 'FORWARD',
  BACKWARD: 'BACKWARD',
};

export default function SpiralModeDialog({ open, scan, symbol, lastClickedBar, onRun, onClose }) {
  const [mode, setMode] = useState(SPIRAL_MODES.UPCOMING);
  const [pivotBar, setPivotBar] = useState('');
  const hasClickedBar = Number.isInteger(lastClickedBar?.index);

  const pivots = useMemo(() => {
    const list = Array.isArray(scan?.topPivots) ? [...scan.topPivots] : [];
    // Offered strongest-first by the API - that is how the shortlist is CHOSEN - but shown newest
    // first, because a reader locating a turn thinks in dates, not in window sizes. The strength is
    // on every row, so nothing is lost by not ordering on it.
    list.sort((a, b) => b.barIndex - a.barIndex);
    return list;
  }, [scan]);

  // Default to the strongest turn rather than leaving the picker empty, so the common case is one
  // click. Re-runs when a new scan arrives, since the previous symbol's bar indices mean nothing here.
  useEffect(() => {
    if (!open) return;
    const strongest = (scan?.topPivots || [])[0];
    setPivotBar(strongest ? String(strongest.barIndex) : '');
    // A mode can outlive its precondition: pick the anchored window, close, load another symbol,
    // reopen. The radio would be disabled AND selected, so Run would fire a mode with no anchor.
    setMode(m => (m === SPIRAL_MODES.UPCOMING_AT_BAR && !hasClickedBar ? SPIRAL_MODES.UPCOMING : m));
  }, [open, scan, hasClickedBar]);

  // Named for the two modes that DO need a pivot, not as "everything except UPCOMING". The
  // negative form silently swept in each new window mode as it was added, and would have put the
  // turn picker under an option that centres on a clicked bar and has no pivot to choose.
  const needsPivot = mode === SPIRAL_MODES.FORWARD || mode === SPIRAL_MODES.BACKWARD;
  const canRun = !needsPivot || pivotBar !== '';
  const near = Array.isArray(scan?.nearTermZones) ? scan.nearTermZones : [];

  const handleRun = () => {
    onRun(mode, needsPivot ? Number(pivotBar) : null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Φ Calendar
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {symbol}
          {scan?.pivotCount != null && ` · ${scan.pivotCount} turns detected`}
          {scan?.barCount != null && ` across ${scan.barCount.toLocaleString()} bars`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel
            value={SPIRAL_MODES.UPCOMING}
            control={<Radio />}
            label={(
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Reversal zones near now
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>
                  {near.length > 0
                    ? `${near.length} convergence${near.length === 1 ? '' : 's'} between `
                      + `${scan?.nearTermWindow?.fromDate} and ${scan?.nearTermWindow?.toDate}`
                      + ` — strongest ${near[0].convergenceCount} turns on ${near[0].targetDate}`
                      + ` (${describeZoneTiming(near[0]) || ''})`
                    : 'No convergence falls in the window around now.'}
                </Typography>
                {/* The backward reach is the part worth stating. A zone that has already passed can
                    be checked against the bars, and a future-only view discarded it at exactly the
                    moment it became verifiable. */}
                <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
                  Each set of agreeing pivots gets its own colour, including zones just passed.
                </Typography>
              </Box>
            )}
          />

          {/*
            The same window, moved.

            Second in the list because it is the same question as the first — where are the
            convergences around a reference point — differing only in which point. The two
            projection modes below ask something else entirely and stay together beneath it.
          */}
          <FormControlLabel
            value={SPIRAL_MODES.UPCOMING_AT_BAR}
            control={<Radio />}
            disabled={!hasClickedBar}
            label={(
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Reversal zones near and after the last bar clicked
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>
                  {hasClickedBar
                    ? `Centres the window on ${lastClickedBar.date}`
                      + ` (bar #${lastClickedBar.index + 1}) instead of on the last bar.`
                    : 'Click a bar on the chart first — there is no anchor to centre on yet.'}
                </Typography>
                {/* The restriction is the point of this option, so it is stated before the run
                    rather than discovered from a smaller number of zones afterwards. */}
                <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
                  Only turns at or before that bar cast a ladder, so the zones after it are
                  projections from past bars rather than a fit to what followed.
                </Typography>
              </Box>
            )}
          />

          <FormControlLabel
            value={SPIRAL_MODES.FORWARD}
            control={<Radio />}
            label={(
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Project forward from a turn
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>
                  Casts this turn&apos;s Fibonacci ladder ahead and reports which rungs landed on a
                  later turn, which landed on nothing, and which are still to come.
                </Typography>
              </Box>
            )}
          />

          <FormControlLabel
            value={SPIRAL_MODES.BACKWARD}
            control={<Radio />}
            label={(
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Trace what points at a turn
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75, display: 'block' }}>
                  The same analysis the upcoming zone gets, aimed at a past bar: every earlier turn
                  whose ladder lands on it, with the step and the error.
                </Typography>
              </Box>
            )}
          />
        </RadioGroup>

        {needsPivot && (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth size="small" disabled={pivots.length === 0}>
              <InputLabel id="spiral-pivot-label">Turn to inspect</InputLabel>
              <Select
                labelId="spiral-pivot-label"
                label="Turn to inspect"
                value={pivotBar}
                onChange={(e) => setPivotBar(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
              >
                {pivots.map((p) => (
                  <MenuItem key={p.barIndex} value={String(p.barIndex)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <span style={{ fontFamily: 'monospace', minWidth: 88 }}>{p.date}</span>
                      <Chip
                        size="small"
                        label={p.type}
                        sx={{
                          height: 18, fontSize: 11,
                          bgcolor: p.type === 'HIGH' ? '#ffebee' : '#e8f5e9',
                          color: p.type === 'HIGH' ? '#c62828' : '#2e7d32',
                        }}
                      />
                      {/* The strength is what separates a major turn from a wiggle, and it is the
                          reason this particular forty were offered. */}
                      <Typography variant="caption" sx={{ opacity: 0.7, fontFamily: 'monospace' }}>
                        N={p.windowN}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {pivots.length === 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                No turns were detected in this series, so there is nothing to inspect. Try a longer
                history or a smaller minWindow.
              </Alert>
            )}

            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
              Showing the {pivots.length} strongest turns, most recent first. To inspect any other
              bar, right-click (or shift-click) it directly on the chart.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleRun} disabled={!canRun}>
          Run
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SpiralModeDialog.propTypes = {
  open: PropTypes.bool,
  /** The /api/spiral/scan response, already fetched so the picker can be populated. */
  scan: PropTypes.object,
  symbol: PropTypes.string,
  /** The last bar clicked on the chart, or null — the anchor for UPCOMING_AT_BAR. */
  lastClickedBar: PropTypes.shape({
    index: PropTypes.number,
    date: PropTypes.string,
  }),
  /** (mode, pivotBarIndex|null) — pivotBarIndex is null for the UPCOMING mode. */
  onRun: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
